/**
 * @constructor
 * @param {HTMLElement|string} container The DOM object in which the accordion will be rendered. Or it can be a jQuery style selector
 * @param {Object} [options] - Optional configuration options.
 * @param {boolean} [options.showExpandCollapseIcon=false] - If <tt>true</tt>, expand/collapse icons will be displayed
 *                                                           next the the heading text
 * @param {expandCollapseCallback} [options.expandListener] - callback fired when the node is expanded
 * @param {expandCollapseCallback} [options.expandCompleteListener] - callback fired when the node finishes expanding
 * @param {expandCollapseCallback} [options.collapseListener] - callback fired when the node is collapsed
 * @param {expandCollapseCallback} [options.collapseCompleteListener] - callback fired when the node finishes collapsing
 *
 * @class
 * This accordion widget is newer and more advanced than the old
 * MITS.Widgets.AnimatedAccordian. It is essentially a wrapper around
 * Bootstrap's accordion component
 *
 * There are two ways to use this widget.  In the basic case, you provide the
 * content, and the widget handles all the events and behavior. In the advanced
 * case, content can be dynamically added and changed, and event listeners can
 * be provided to react to the expand and collapse events.
 *
 * Terminology
 * -----------
 * The heading is part of the widget the user clicks on to expand or contract
 * the accordion. It contains text and is uniquely identified by that text
 * (meaning two headings can't have the same text).
 *
 * The content is a DOM element that will be displayed below the heading. It is
 * allowed to have an ID that will not be overridden.
 *
 *
 * Example Usage
 * -------------
 * In the basic case, the JSP generates HTML for the contents of the widget.
 * (Tip: Put the contents inside a hidden div so it doesn't appear until the
 *    accordion is rendered).
 * Then JavaScript is used to create the widget and calls setHeadings,
 * providing heading titles and the IDs of the content elements.
 * The widget will create new DOM elements, and move the content elements into
 * the widget.
 */
'use strict';

var BootstrapAccordion = function (container, options) {
    this.initialize(container, options);
};

var NodeHandle = function($contentDiv, $expandCollapseIcon, heading) {
    this.$contentDiv = $contentDiv;
    this.$expandCollapseIcon = $expandCollapseIcon;
    this.heading = heading;
};

/**
 * @callback expandCollapseCallback
 * @param {string} heading - clicked heading
 */

NodeHandle.prototype = {
    setHeadingContent: function ($content) {
        this.$contentDiv.empty();
        this.$contentDiv.append($content);
    },

    expand: function () {
        this.$contentDiv.collapse('show');
    },

    collapse: function () {
        this.$contentDiv.collapse('hide');
    },

    addExpandCollapseListener: function(collapseExpandListener) {
        if (this.$expandCollapseIcon) {
            this.$contentDiv.on('show.bs.collapse', this._updateHeaderIconForExpand.bind(this));
            this.$contentDiv.on('hide.bs.collapse', this._updateHeaderIconForCollapse.bind(this));
        }
        this.$contentDiv.on('show.bs.collapse',
            collapseExpandListener._onExpanding.bind(collapseExpandListener, this.heading));
        this.$contentDiv.on('shown.bs.collapse',
            collapseExpandListener._onExpandComplete.bind(collapseExpandListener, this.heading));
        this.$contentDiv.on('hide.bs.collapse',
            collapseExpandListener._onCollapsing.bind(collapseExpandListener, this.heading));
        this.$contentDiv.on('hidden.bs.collapse',
            collapseExpandListener._onCollapseComplete.bind(collapseExpandListener, this.heading));
    },

    _updateHeaderIconForExpand: function(event) {
        if (event.target === event.currentTarget) {
            this.$expandCollapseIcon.removeClass('fa-caret-right');
            this.$expandCollapseIcon.addClass('fa-caret-down');
        }
    },

    _updateHeaderIconForCollapse: function(event) {
        if (event.target === event.currentTarget) {
            this.$expandCollapseIcon.addClass('fa-caret-right');
            this.$expandCollapseIcon.removeClass('fa-caret-down');
        }
    }
};

BootstrapAccordion._instanceCount = 1;
BootstrapAccordion.prototype = {
    _showExpandCollapseIcon: false,
    _headingToNodeHandle: {},

    initialize: function(container, options) {
        this._$container = jQuery(container);
        var instanceNum = BootstrapAccordion._instanceCount++;
        this._contentIdPrefix = 'content_' + instanceNum + '_';
        this._headingIdPrefix = 'heading_' + instanceNum + '_';
        this._accordionId = 'accordion_' + instanceNum;
        this._headingIdCount = 0;
        this._contentIdCount = 0;
        this._expandedHeadingsSet = {};

        if (options) {
            if (options.showExpandCollapseIcon) {
                this._showExpandCollapseIcon = true;
            }

            if (options.expandListener) {
                this._expandListener = options.expandListener;
            }

            if (options.collapseListener) {
                this._collapseListener = options.collapseListener;
            }

            if (options.expandCompleteListener) {
                this._expandCompleteListener = options.expandCompleteListener;
            }

            if (options.collapseCompleteListener) {
                this._collapseCompleteListener = options.collapseCompleteListener;
            }
        }
    },

    /**
     * Specifies the headings and the content as parallel arrays.
     * This function can be called multiple times to dynamically
     * clear out the headings (and content) and insert new ones.
     *
     * @param headingArray An array of Strings that will be heading titles.
     *        This is required.
     * @param contentArray An array of DOM elements or element IDs. This
     *        is optional, since content can be provided later.
     *
     * Note: This modifies the DOM, and can't be called until the page
     * is fully loaded.
     */
    setHeadings: function (headingArray, contentArray) {
        this._headingToNodeHandle = {};
        this.$root = jQuery('<div class="panel-group" id="' + this._accordionId + '" role="tablist" aria-multiselectable="true"></div>');

        for (var i = 0; i < headingArray.length; i++) {
            var heading = headingArray[i];
            var handle = this._addHeadingToContainer(heading, contentArray[i]);
            handle.addExpandCollapseListener(this);
            this._headingToNodeHandle[heading] = handle;
        }

        var $wrapper = jQuery('<div class="bootstrap-accordion">');
        this.$root.appendTo($wrapper);
        $wrapper.appendTo(this._$container);
    },

    /**
     * Sets the content for a specific heading. Note that setHeadings
     * must be called first.
     *
     * @param heading - a heading string that was previously passed
     *        to the setHeadings function.
     * @param content - a DOM element or element ID
     */
    setHeadingContent: function (heading, content) {
        var handle = this._headingToNodeHandle[heading];
        var $bodyDiv = this._create$ContentCell(content);
        handle.setHeadingContent($bodyDiv);
    },

    /**
     * Gets and array containing the text of the currently expanded headings.
     * They are in order from oldest to most recently expanded.
     */
    getExpandedHeadings: function () {
        return Object.keys(this._expandedHeadingsSet);
    },

    /**
     * @param heading - The text of the heading to expand.
     */
    expand: function (heading) {
        var handle = this._headingToNodeHandle[heading];
        handle.expand();
    },

    /**
     * @param heading - The text of the heading to collapse.
     */
    collapse: function (heading) {
        var handle = this._headingToNodeHandle[heading];
        handle.collapse();
    },

    collapseAll: function() {
        var expandedHeadings = this.getExpandedHeadings();

        for(var i=0;i<expandedHeadings.length;i++){
            var heading = expandedHeadings[i];
            this.collapse(heading);
        }
    },

    _addHeadingToContainer:function(heading, content) {
        var containerId = this._accordionId;
        var id = heading.replace(new RegExp('[^A-Za-z0-9]', 'g'), '_');
        var headingId = this._headingIdPrefix + id + this._headingIdCount++;
        var contentId = this._contentIdPrefix + id + this._contentIdCount++;

        var handles = this._create$HeadingDiv(headingId, containerId, contentId, heading);
        var $headingDiv = handles.$headingDiv;
        var $expandCollapsIcon = handles.$expandCollapsIcon;
        var $bodyDiv = this._create$ContentCell(content);

        var $contentDiv = jQuery('<div id="' + contentId + '" class="panel-collapse collapse" role="tabpanel" aria-labelledby="' + headingId + '"></div>');
        $contentDiv.append($bodyDiv);

        var $entryDiv = jQuery('<div class="panel panel-default"></div>');
        $entryDiv.append($headingDiv);
        $entryDiv.append($contentDiv);
        this.$root.append($entryDiv);
        return new NodeHandle($contentDiv, $expandCollapsIcon, heading);
    },

    _create$HeadingDiv: function(headingId, containerId, contentId, heading) {
        var $heading = jQuery('<div class="panel-heading" role="tab" id="' + headingId + '"></div>');
        var $h4 = jQuery('<h4 class="panel-title"></h4>');
        $heading.append($h4);
        var $anchor = jQuery('<a role="button" class="collapsed" aria-expanded="false" data-toggle="collapse" data-parent="#' + containerId + '" href="#' + contentId + '" aria-expanded="true" aria-controls="collapseOne"></a>');
        $h4.append($anchor);
        var $expandCollapsIcon = null;
        if (this._showExpandCollapseIcon) {
            $expandCollapsIcon = jQuery('<span class="fa fa-fw fa-caret-right" aria-hidden="true"></span>');
            $anchor.append($expandCollapsIcon);
            $anchor.append('<span>' + heading + '</span>');
        } else {
            $anchor.text(heading);
        }
        return { $headingDiv: $heading, $expandCollapsIcon: $expandCollapsIcon };
    },

    _onExpanding: function(heading, e) {
        this._expandedHeadingsSet[heading] = true;
        if(this._expandListener) {
            this._expandListener(heading, e);
        }
    },

    _onExpandComplete: function(heading, e) {
        if(this._expandCompleteListener) {
            this._expandCompleteListener(heading, e);
        }
    },

    _onCollapsing: function(heading, e) {
        delete this._expandedHeadingsSet[heading];
        if(this._collapseListener) {
            this._collapseListener(heading, e);
        }
    },

    _onCollapseComplete: function(heading, e) {
        if(this._collapseCompleteListener) {
            this._collapseCompleteListener(heading, e);
        }
    },

    _create$ContentCell: function(content) {
        var $bodyDiv = jQuery('<div class="panel-body mits-panel-no-body-padding"></div>');
        if (typeof content === 'string') {
            $bodyDiv.text(content);
        } else {
            $bodyDiv.append(content);
        }
        return $bodyDiv;
    }
};


module.exports = BootstrapAccordion;


//////////////////
// WEBPACK FOOTER
// ./mits/Widgets/BootstrapAccordion.js
// module id = 16
// module chunks = 0